import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useTenant } from '../contexts/TenantContext';
import { gradientFillSx, gradientTextSx } from '../utils/textStyles';

/**
 * A filled button: an action carries the same gradient the headings do. Exported for the buttons
 * that are not a CustomButton (an icon at the start, a test id), so every filled action of the
 * site, the dashboard's included, is painted alike. `color` defaults to the tenant's button
 * colour, `textColor` to its button text colour.
 */
export const filledButtonSx = (displayConfig, color = displayConfig?.buttonColor,
  textColor = displayConfig?.buttonTextColor) => {
  const fill = gradientFillSx(displayConfig, color);

  return {
    ...fill,
    color: textColor,
    '&:hover': { ...fill['&:hover'], color: textColor },
    // Loading disables the button; MUI would grey it out with it, fill and spinner alike, and
    // a grey button reads as a broken one rather than a busy one. It keeps its fill, dimmed.
    '&.Mui-disabled': {
      backgroundColor: fill.backgroundColor,
      backgroundImage: fill.backgroundImage,
      color: textColor,
      opacity: 0.7,
    },
  };
};

const CustomButton = ({
  variant = 'contained',
  onClick,
  isLoading = false,
  disabled = false,
  color,
  text = null,
  textTransform = 'capitalize',
  mt = 0,
  type = "button",
  width,
  fullwidth = false,
  textColor,
  icon = null,
  arrowIcon = null
}) => {
  const { displayConfig } = useTenant();
  const isContained = variant === 'contained';
  const buttonColor = color || displayConfig?.buttonColor;
  const buttonTextColor = isContained
    ? textColor || displayConfig?.buttonTextColor
    : textColor || displayConfig?.primaryColor;

  const containedSx = filledButtonSx(displayConfig, buttonColor, buttonTextColor);

  const outlinedSx = {
    color: buttonTextColor,
    borderColor: buttonTextColor,
    '&:hover': {
      borderColor: buttonTextColor,
      backgroundColor: `color-mix(in srgb, ${buttonTextColor} 8%, transparent)`,
    },
    '&.Mui-disabled': { color: buttonTextColor, borderColor: buttonTextColor, opacity: 0.7 },
  };

  // An outlined button is lettered in the gradient.
  // The letters are gradient-filled on the label rather than on the button itself, and that
  // is not a detail: `background-clip: text` clips *every* background of the element it is
  // set on, so a button carrying it would lose its own hover wash and its icon with it.
  // The icon is an svg and takes the flat colour from the button either way; only the
  // label is filled with the gradient.
  const labelSx = isContained
    ? { color: buttonTextColor }
    : gradientTextSx(displayConfig, buttonTextColor);

  return (
    <Box display="flex" justifyContent="center" mt={mt} width={width}>
      <Button
        variant={variant}
        onClick={onClick}
        disabled={isLoading || disabled}
        aria-busy={isLoading}
        // The hidden label no longer names the button, so the name is given here meanwhile.
        aria-label={isLoading ? text?.replaceAll("<br />", " ") : undefined}
        type={type}
        fullWidth={width ? true : fullwidth}
        sx={isContained ? containedSx : outlinedSx}>
        {/* While loading, the label keeps its place but turns invisible, so the button keeps its
            size, and the spinner sits over it. */}
        <Box display="flex" alignItems="center" gap={1} visibility={isLoading ? 'hidden' : 'visible'}>
          {icon}
          <Typography fontWeight="bold" textTransform={textTransform} sx={labelSx}>
            {text?.split("<br />").map((t, index) =>
              <React.Fragment key={index}>
                {t} <br />
              </React.Fragment>
            )}
          </Typography>
          {arrowIcon}
        </Box>
        {isLoading && (
          <CircularProgress
            size={20}
            color="inherit"
            sx={{ position: 'absolute', top: 'calc(50% - 10px)', left: 'calc(50% - 10px)' }}
          />
        )}
      </Button>
    </Box>
  );
};

export default CustomButton;
